import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import {
    FiAlertCircle,
    FiArrowRight,
    FiCalendar,
    FiCamera,
    FiCheckCircle,
    FiChevronDown,
    FiClock,
    FiDownload,
    FiEye,
    FiEyeOff,
    FiFileText,
    FiInbox,
    FiInfo,
    FiKey,
    FiLock,
    FiLogIn,
    FiMail,
    FiMaximize2,
    FiRefreshCw,
    FiShield,
    FiSlash,
    FiSmartphone,
    FiUser,
    FiX,
    FiXCircle,
} from "react-icons/fi";
import { apiService, readBlobErrorMessage } from "../../api/apiService";
import { passVerifyUrl } from "../../utils/routeHelpers";
import { useToast } from "../../context/ToastContext";
import PageHero from "../../components/common/PageHero";
import LegalDrawer from "../../components/legal/LegalDrawer";
import PortalShell from "./PortalShell";
import { formatBirthDatePl, formatDatePl } from "./portalValidation";
import styles from "./PortalProfilePage.module.css";

/*
 * PROFIL OSOBY ZEWNĘTRZNEJ — „moje konto" w portalu Biura Przepustek.
 *
 * Trzy sekcje wynikają wprost z tego, po co gość tu wchodzi:
 *   1. DANE OSOBOWE — tylko do odczytu. Imię, nazwisko, numer dokumentu i data urodzenia trafiają
 *      na dokument przepustki i na portiernię, więc portal ich NIE edytuje; poprawkę zgłasza
 *      się Biuru Przepustek. Regułę podaje backend (pole `dataChange`), front jej nie kopiuje.
 *   2. ZGODY — co i kiedy zostało zaakceptowane, z możliwością ponownego przeczytania
 *      dokumentu w tym samym panelu (LegalDrawer), którym akceptowało się je we wniosku.
 *   3. HISTORIA PRZEPUSTEK — status wniosku ORAZ status ważności (aktywna / nadchodząca /
 *      zakończona / anulowana). Oba liczy serwer: od tej etykiety zależy, czy ktoś wsiądzie
 *      w samochód i przyjedzie pod bramę, a zegar urządzenia bywa przestawiony.
 *
 * Dane wrażliwe (numer dokumentu, data urodzenia) przychodzą wyłącznie zamaskowane —
 * PESEL usunięty z całej aplikacji decyzją klienta 2026-08-22. Pełną wartość
 * oddaje osobny endpoint „reveal", którego KAŻDE wywołanie serwer zapisuje w dzienniku —
 * dlatego wołamy go tylko na świadome kliknięcie ikony oka, nigdy przy wczytywaniu strony.
 *
 * Własny moduł CSS (a nie wspólny PortalPages.module.css) jest świadomy: profil to jedyny
 * widok portalu za logowaniem i ma własne komponenty (wiersze danych z „oczkiem", karty
 * historii). Język wizualny pozostaje ten sam — docs/DESIGN-SYSTEM-PODSTRON.md.
 */

const STATUS_TONE = {
    0: "neutral", // Szkic
    1: "warn", // Złożony — czeka na decyzję
    2: "ok", // Zaakceptowany
    3: "bad", // Odrzucony
    4: "bad", // Anulowany
    5: "neutral", // Zakończony
};

const VALIDITY_TONE = {
    aktywna: "ok",
    nadchodzaca: "info",
    zakonczona: "neutral",
    anulowana: "bad",
    odrzucona: "bad",
    oczekuje: "warn",
};

const FILTERS = [
    { key: "all", label: "Wszystkie" },
    { key: "aktywna", label: "Aktywne" },
    { key: "nadchodzaca", label: "Nadchodzące" },
    { key: "oczekuje", label: "Oczekujące" },
    { key: "zakonczona", label: "Zakończone" },
];

/// Pełne wartości znikają z ekranu same po tym czasie — profil bywa otwarty na komputerze
/// w recepcji albo na telefonie oddanym ochronie do okazania kodu QR.
const REVEAL_TIMEOUT_MS = 45000;

/// Znaczniki czasu z API pokazujemy przez toLocaleString — tak samo jak strona statusu wniosku.
const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pl-PL");
};

/// Dla zgód wystarcza sama data: znaczniki akceptacji zapisywane są w UTC, więc godzina
/// mogłaby się przesunąć o strefę, a data akceptacji to jedyne, co ma tu znaczenie.
const formatDateOnly = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pl-PL");
};

/// Zapis pliku przez tymczasowy odnośnik <a download> — ten sam wzorzec co w podglądzie
/// dokumentów w PortalAccountsPage.
const triggerDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Odwołanie natychmiast po click() potrafi ubiec zapis pliku w Safari — dajemy zapas.
    setTimeout(() => URL.revokeObjectURL(url), 5000);
};

/// Nazwa pliku potwierdzenia zgód — numer wniosku bez znaków spoza nazw plików.
const consentPdfFileName = (data) =>
    `Zgody-${String(data?.number || "wniosek").replace(/[^\w.-]+/g, "_")}.pdf`;

/// Token śledzenia wyciągamy z adresów zwróconych przez API zamiast prosić backend o samą
/// wartość: dzięki temu PDF pobieramy istniejącą metodą (blob, bez tokenu w pasku adresu
/// i w historii przeglądarki), a podgląd statusu otwieramy trasą wewnątrz aplikacji.
const trackingTokenOf = (item) => {
    const source = item?.pdfUrl || item?.trackingUrl || "";
    const match = /\/(?:requests|status)\/([^/?#]+)/.exec(String(source));
    return match ? decodeURIComponent(match[1]) : null;
};

/// Dlaczego przycisk PDF jest nieaktywny — komunikat zamiast samego wyszarzenia.
/// Regułę („po akceptacji, do N godzin po zakończeniu") narzuca backend; tu tylko tłumaczymy.
const pdfHint = (item, graceHours) => {
    if (item.canDownloadPdf) {
        return item.pdfAvailableUntil
            ? `Dokument dostępny do ${formatDateTime(item.pdfAvailableUntil)}.`
            : "Dokument gotowy do pobrania.";
    }
    if (item.status === 0 || item.status === 1) {
        return "Dokument przepustki powstaje po akceptacji wniosku przez Biuro Przepustek.";
    }
    if (item.status === 3) {
        return "Wniosek został odrzucony — dokument przepustki nie powstał.";
    }
    if (item.status === 4) {
        return "Wniosek anulowany — przepustka nie jest ważna.";
    }
    return item.pdfAvailableUntil
        ? `Pobieranie było możliwe do ${formatDateTime(item.pdfAvailableUntil)} (${graceHours} godz. po zakończeniu wizyty).`
        : "Termin pobrania dokumentu minął.";
};

/// Wiersz definicji danych osobowych: etykieta | wartość | „oczko" w JEDNYM wierszu,
/// wiersze oddziela cienka linia (biznesowy zapis zamiast kafelków-kartoników —
/// uwaga klienta 2026-08-08). Poza komponentem strony, żeby zmiana stanu
/// (odsłonięcie numeru dokumentu) nie odmontowywała pozostałych wierszy.
const DataRow = ({ label, value, mono = false, action = null, note = null }) => (
    <div className={styles.dataRow}>
        <dt className={styles.dataTerm}>{label}</dt>
        <dd className={styles.dataCell}>
            <span className={styles.dataValueWrap}>
                <span className={`${styles.dataValue} ${mono ? styles.dataMono : ""}`}>{value}</span>
                {action}
            </span>
            {note && <span className={styles.dataNote}>{note}</span>}
        </dd>
    </div>
);

/**
 * `embedded` = sekcja MOJE DANE na pulpicie wizytatora (decyzja klienta 2026-08-08 —
 * osobna strona portalowa zlikwidowana). Logika bez zmian: edycja danych dopiero po
 * potwierdzeniu konta kodem SMS; zmiana danych dokumentu wymaga przeslania dokumentu
 * do weryfikacji. Znika wylacznie rama portalu.
 */
const PortalProfilePage = ({ embedded = false }) => {
    const { success, error: toastError, info } = useToast();

    const [me, setMe] = useState(null);
    const [requests, setRequests] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authRequired, setAuthRequired] = useState(false);
    const [blocked, setBlocked] = useState("");
    const [error, setError] = useState("");
    const [requestsError, setRequestsError] = useState("");

    // Odsłonięte dane wrażliwe: jedno wywołanie API oddaje numer dokumentu i datę urodzenia
    // naraz (PESEL usunięty decyzją klienta 2026-08-22), więc drugie „oczko" korzysta z już
    // pobranej wartości — nie dokładamy do dziennika wpisu za coś, co użytkownik
    // i tak przed chwilą zobaczył.
    const [revealed, setRevealed] = useState(null);
    const [shown, setShown] = useState({ birthDate: false, documentNumber: false, phone: false });
    const [revealBusy, setRevealBusy] = useState(false);
    const revealTimerRef = useRef(null);

    const [legalKind, setLegalKind] = useState(null);
    const [filter, setFilter] = useState("all");
    const [pdfBusy, setPdfBusy] = useState("");

    // Zgody z datami akceptacji i id wniosku do PDF-u potwierdzenia (GET /portal/me/consents).
    // `missing` = 404: osoba nie ma jeszcze wniosku ze zgodami (cichy stan zamiast błędu).
    const [myConsents, setMyConsents] = useState(null);
    const [myConsentsMissing, setMyConsentsMissing] = useState(false);

    // Podgląd PDF potwierdzenia zgód (modal). Adres blob sprząta efekt przy podmianie
    // i odmontowaniu; okno zamykają WYŁĄCZNIE przyciski (warunek W-10).
    const [consentPreview, setConsentPreview] = useState(null);
    const [consentPdfBusy, setConsentPdfBusy] = useState(""); // "" | "preview" | "download"

    // Zakładki profilu: „Przepustki" (historia + kod QR aktywnej) i „Moje dane" (dane, zgody,
    // weryfikacja e-mail/telefonu, zmiana hasła). Domyślnie przepustki — po to gość tu wchodzi.
    // W trybie embedded (panel Moje dane na pulpicie) od razu zakladka danych —
    // historia przepustek mieszka na pulpicie, nie w tym panelu.
    const [tab, setTab] = useState(embedded ? "dane" : "przepustki");

    // Podgląd kodu QR aktywnej przepustki na pełny ekran (jak na stronie statusu wniosku).
    const [qrPass, setQrPass] = useState(null);
    const qrCanvasRef = useRef(null);

    // Weryfikacja e-mail / telefonu z zakładki „Moje dane".
    const [emailStage, setEmailStage] = useState("idle"); // idle | code
    const [emailCode, setEmailCode] = useState("");
    const [emailBusy, setEmailBusy] = useState("");
    const [phoneStage, setPhoneStage] = useState("idle");
    const [phoneCode, setPhoneCode] = useState("");
    const [phoneBusy, setPhoneBusy] = useState("");

    // Zmiana hasła.
    const [pwCurrent, setPwCurrent] = useState("");
    const [pwNew, setPwNew] = useState("");
    const [pwConfirm, setPwConfirm] = useState("");
    const [pwBusy, setPwBusy] = useState(false);
    const [pwError, setPwError] = useState("");
    // Sekcja „Bezpieczeństwo konta" (zmiana hasła) startuje ZWINIĘTA — hasło zmienia się
    // raz na kwartał, a trzy pola formularza na stałe wydłużały panel o pół ekranu.
    const [securityOpen, setSecurityOpen] = useState(false);

    // Zdjęcie dokumentu właściciela konta — blob przez axios (autoryzacja cookie),
    // 404 = wnioski bez zdjęcia (pokazujemy miejsce „brak zdjęcia" zamiast błędu).
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoMissing, setPhotoMissing] = useState(false);

    const load = useCallback(
        async (manual = false) => {
            setLoading(true);
            const [profileResult, requestsResult] = await Promise.allSettled([
                apiService.getPortalMe(),
                apiService.getPortalMyRequests(),
            ]);

            if (profileResult.status === "fulfilled") {
                setMe(profileResult.value.data);
                setAuthRequired(false);
                setBlocked("");
                setError("");
                if (manual) success("Profil zaktualizowany.");
            } else {
                const err = profileResult.reason;
                const status = err?.response?.status;
                setMe(null);
                if (status === 401) {
                    setAuthRequired(true);
                    setError("");
                } else if (status === 403) {
                    setBlocked(
                        err.response?.data?.message ||
                            "Twoje konto portalowe jest zablokowane — napisz na biuroprzepustek@krosno.com."
                    );
                } else {
                    const message =
                        err?.response?.data?.message ||
                        err?.friendlyMessage ||
                        "Nie udało się wczytać profilu.";
                    setError(message);
                    if (manual) toastError(message);
                }
            }

            if (requestsResult.status === "fulfilled") {
                setRequests(requestsResult.value.data);
                setRequestsError("");
            } else {
                setRequests(null);
                // 401/403 obsługuje już sekcja profilu — nie dublujemy tego samego komunikatu.
                const status = requestsResult.reason?.response?.status;
                setRequestsError(
                    status === 401 || status === 403
                        ? ""
                        : "Nie udało się wczytać historii przepustek."
                );
            }

            setLoading(false);
        },
        [success, toastError]
    );

    useEffect(() => {
        load();
    }, [load]);

    // Sprzątanie licznika auto-ukrywania: bez tego timer po odmontowaniu strony wołałby
    // setState na komponencie, którego już nie ma.
    useEffect(
        () => () => {
            if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
        },
        []
    );

    // Zdjęcie dociągamy DOPIERO gdy profil się wczytał (endpoint i tak wymaga tej samej
    // sesji) i sprzątamy object URL przy odmontowaniu — blob w pamięci przeglądarki nie
    // zwalnia się sam. `cancelled` chroni przed setState po zamknięciu panelu w trakcie
    // pobierania.
    useEffect(() => {
        if (!me) return undefined;
        let cancelled = false;
        let objectUrl = null;
        apiService
            .getPortalMyPhoto()
            .then((response) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(response.data);
                setPhotoUrl(objectUrl);
                setPhotoMissing(false);
            })
            .catch(() => {
                if (cancelled) return;
                setPhotoUrl(null);
                setPhotoMissing(true);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [me]);

    // Daty akceptacji zgód dociągamy po wejściu w zakładkę „Moje dane" (w panelu
    // embedded — od razu). BEZ strażnika w ref: StrictMode montuje efekty dwukrotnie,
    // a ref przeżywa ten cykl — pierwsze pobranie było odrzucane przez `cancelled`,
    // drugie nigdy nie startowało i przyciski PDF nie miały danych. GET jest
    // idempotentny, więc ewentualne powtórzenie po zmianie `me` nic nie psuje.
    // 404 = osoba nie ma jeszcze wniosku ze zgodami — cichy stan, nie błąd.
    useEffect(() => {
        if (tab !== "dane" || !me) return undefined;
        let cancelled = false;
        apiService
            .getPortalMyConsents()
            .then((response) => {
                if (cancelled) return;
                setMyConsents(response.data);
                setMyConsentsMissing(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setMyConsents(null);
                setMyConsentsMissing(err?.response?.status === 404);
            });
        return () => {
            cancelled = true;
        };
    }, [tab, me]);

    // Adres blob podglądu PDF zgód zwalniamy przy każdej podmianie i przy odmontowaniu —
    // obiekt w pamięci przeglądarki nie zwalnia się sam.
    useEffect(() => {
        if (!consentPreview?.url) return undefined;
        const { url } = consentPreview;
        return () => URL.revokeObjectURL(url);
    }, [consentPreview]);

    /**
     * Odsłonięcie własnych danych wymaga kodu z e-maila KONTA (decyzja klienta 2026-08-08):
     * sesja bywa porzucona na wspólnym sprzęcie, więc sama nie wystarcza do odczytu danych,
     * które trafiają na dokumenty. `revealChallenge` = czekamy na kod; `pendingField` =
     * pole, które użytkownik chciał zobaczyć (odsłoni się od razu po poprawnym kodzie).
     */
    const [revealChallenge, setRevealChallenge] = useState(null);
    const [revealCode, setRevealCode] = useState("");
    const [pendingField, setPendingField] = useState(null);

    const scheduleHide = useCallback(() => {
        if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
        revealTimerRef.current = setTimeout(() => {
            setShown({ birthDate: false, documentNumber: false, phone: false });
            setRevealed(null);
        }, REVEAL_TIMEOUT_MS);
    }, []);

    const toggleField = useCallback(
        async (field) => {
            if (shown[field]) {
                setShown((prev) => ({ ...prev, [field]: false }));
                return;
            }
            // Telefon przychodzi z API w całości (to własny numer właściciela konta) — maska
            // jest tylko zasłoną na ekranie, więc jej zdjęcie nie wymaga wywołania „reveal".
            if (field === "phone") {
                setShown((prev) => ({ ...prev, phone: true }));
                scheduleHide();
                return;
            }
            if (revealed) {
                setShown((prev) => ({ ...prev, [field]: true }));
                scheduleHide();
                return;
            }
            // Krok 1: poproś o kod na e-mail konta — pola odsłonią się dopiero po kroku 2.
            setRevealBusy(true);
            try {
                const { data } = await apiService.sendPortalRevealCode();
                setRevealChallenge(data);
                setRevealCode("");
                setPendingField(field);
            } catch (err) {
                toastError(
                    err.response?.data?.message ||
                        err.friendlyMessage ||
                        "Nie udało się wysłać kodu na adres e-mail konta."
                );
            } finally {
                setRevealBusy(false);
            }
        },
        [shown, revealed, scheduleHide, toastError]
    );

    // Krok 2: kod z e-maila → pełne wartości.
    const confirmRevealCode = useCallback(async () => {
        const code = revealCode.replace(/\D/g, "");
        if (code.length !== 6) {
            toastError("Kod ma 6 cyfr.");
            return;
        }
        setRevealBusy(true);
        try {
            const { data } = await apiService.revealPortalMyProfile(code);
            setRevealed(data);
            if (pendingField) {
                setShown((prev) => ({ ...prev, [pendingField]: true }));
            }
            scheduleHide();
            setRevealChallenge(null);
            setRevealCode("");
            setPendingField(null);
            info("Odsłonięcie danych zostało zapisane w dzienniku dostępu.");
        } catch (err) {
            toastError(
                err.response?.data?.message ||
                    err.friendlyMessage ||
                    "Kod jest nieprawidłowy albo wygasł."
            );
        } finally {
            setRevealBusy(false);
        }
    }, [revealCode, pendingField, scheduleHide, info, toastError]);

    const items = useMemo(() => requests?.items || [], [requests]);
    const visibleItems = useMemo(
        () => (filter === "all" ? items : items.filter((item) => item.validity === filter)),
        [items, filter]
    );

    const downloadPdf = useCallback(
        async (item) => {
            const token = trackingTokenOf(item);
            if (!token) {
                toastError("Dla tego wniosku nie ma dokumentu do pobrania.");
                return;
            }
            setPdfBusy(item.number);
            try {
                const response = await apiService.downloadPortalRequestPdf(token);
                const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                const link = document.createElement("a");
                link.href = url;
                link.download = `Przepustka-${String(item.number || "wniosek").replace(/[^\w.-]+/g, "_")}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                // Adres blob zwalniamy dopiero po chwili — Safari potrafi przerwać pobieranie,
                // jeśli obiekt zniknie w tej samej klatce, w której kliknięto odnośnik.
                setTimeout(() => URL.revokeObjectURL(url), 4000);
                success("PDF przepustki pobrany.");
            } catch (err) {
                toastError(await readBlobErrorMessage(err, "Nie udało się pobrać PDF przepustki."));
            } finally {
                setPdfBusy("");
            }
        },
        [success, toastError]
    );

    // ── PDF potwierdzenia zgód (RODO + regulamin z datami akceptacji) ──
    // Dokument generuje backend z wniosku `requestId` — ta sama ścieżka co przy zapisie
    // na dysk, więc audyt po stronie serwera obejmuje także podgląd.
    const previewConsentPdf = useCallback(async () => {
        if (!myConsents?.requestId) return;
        setConsentPdfBusy("preview");
        try {
            const response = await apiService.downloadPassPrivacyPdf(myConsents.requestId);
            const blob = new Blob([response.data], { type: "application/pdf" });
            setConsentPreview({
                title: "Zgody i oświadczenia",
                subtitle: myConsents.number ? `wniosek ${myConsents.number}` : "",
                url: URL.createObjectURL(blob),
                blob,
                fileName: consentPdfFileName(myConsents),
            });
        } catch (err) {
            toastError(await readBlobErrorMessage(err, "Nie udało się otworzyć potwierdzenia zgód."));
        } finally {
            setConsentPdfBusy("");
        }
    }, [myConsents, toastError]);

    const downloadConsentPdf = useCallback(async () => {
        if (!myConsents?.requestId) return;
        setConsentPdfBusy("download");
        try {
            const response = await apiService.downloadPassPrivacyPdf(myConsents.requestId);
            triggerDownload(
                new Blob([response.data], { type: "application/pdf" }),
                consentPdfFileName(myConsents)
            );
            success("Potwierdzenie zgód pobrane.");
        } catch (err) {
            toastError(await readBlobErrorMessage(err, "Nie udało się pobrać potwierdzenia zgód."));
        } finally {
            setConsentPdfBusy("");
        }
    }, [myConsents, success, toastError]);

    const closeConsentPreview = useCallback(() => setConsentPreview(null), []);

    // ── Kod QR aktywnej przepustki (pełny ekran) ──
    useEffect(() => {
        if (!qrPass?.qrToken || !qrCanvasRef.current) return;
        const side = Math.max(240, Math.min(Math.min(window.innerWidth, window.innerHeight) - 96, 460));
        QRCode.toCanvas(qrCanvasRef.current, passVerifyUrl(qrPass.qrToken), {
            width: side,
            margin: 2,
            errorCorrectionLevel: "M",
            color: { dark: "#0f172a", light: "#ffffff" },
        }).catch(() => {});
    }, [qrPass]);

    useEffect(() => {
        if (!qrPass) return undefined;
        const onKey = (event) => {
            if (event.key === "Escape") setQrPass(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [qrPass]);

    // ── Weryfikacja e-mail ──
    const sendEmailCode = useCallback(async () => {
        setEmailBusy("send");
        try {
            const { data } = await apiService.sendPortalMyEmailCode();
            if (data?.alreadyVerified) {
                await load();
                success("Adres e-mail jest już potwierdzony.");
                return;
            }
            setEmailStage("code");
            setEmailCode("");
            success(data?.devCode ? `Kod (dev): ${data.devCode}` : "Wysłaliśmy kod na Twój adres e-mail.");
        } catch (err) {
            toastError(err.response?.data?.message || "Nie udało się wysłać kodu na e-mail.");
        } finally {
            setEmailBusy("");
        }
    }, [load, success, toastError]);

    const verifyEmail = useCallback(async () => {
        if (emailCode.trim().length < 4) {
            toastError("Wpisz kod z wiadomości e-mail.");
            return;
        }
        setEmailBusy("verify");
        try {
            await apiService.verifyPortalMyEmail(emailCode.trim());
            setEmailStage("idle");
            setEmailCode("");
            await load();
            success("Adres e-mail został potwierdzony.");
        } catch (err) {
            toastError(err.response?.data?.message || "Nie udało się potwierdzić adresu e-mail.");
        } finally {
            setEmailBusy("");
        }
    }, [emailCode, load, success, toastError]);

    // ── Weryfikacja telefonu ──
    const sendPhoneCode = useCallback(async () => {
        setPhoneBusy("send");
        try {
            const { data } = await apiService.sendPortalMyPhoneCode();
            if (data?.alreadyVerified) {
                await load();
                success("Numer telefonu jest już potwierdzony.");
                return;
            }
            setPhoneStage("code");
            setPhoneCode("");
            success(data?.devCode ? `Kod (dev): ${data.devCode}` : "Wysłaliśmy kod SMS na Twój numer.");
        } catch (err) {
            toastError(err.response?.data?.message || "Nie udało się wysłać kodu SMS.");
        } finally {
            setPhoneBusy("");
        }
    }, [load, success, toastError]);

    const verifyPhone = useCallback(async () => {
        if (phoneCode.trim().length < 4) {
            toastError("Wpisz kod z wiadomości SMS.");
            return;
        }
        setPhoneBusy("verify");
        try {
            await apiService.verifyPortalMyPhone(phoneCode.trim());
            setPhoneStage("idle");
            setPhoneCode("");
            await load();
            success("Numer telefonu został potwierdzony.");
        } catch (err) {
            toastError(err.response?.data?.message || "Nie udało się potwierdzić numeru telefonu.");
        } finally {
            setPhoneBusy("");
        }
    }, [phoneCode, load, success, toastError]);

    // ── Zmiana hasła ──
    const changePassword = useCallback(
        async (event) => {
            event.preventDefault();
            setPwError("");
            if (!pwCurrent || !pwNew) {
                setPwError("Podaj obecne i nowe hasło.");
                return;
            }
            if (pwNew.length < 8) {
                setPwError("Nowe hasło musi mieć co najmniej 8 znaków.");
                return;
            }
            if (pwNew !== pwConfirm) {
                setPwError("Nowe hasło i jego powtórzenie różnią się.");
                return;
            }
            setPwBusy(true);
            try {
                await apiService.changePortalMyPassword(pwCurrent, pwNew);
                setPwCurrent("");
                setPwNew("");
                setPwConfirm("");
                success("Hasło zostało zmienione.");
            } catch (err) {
                setPwError(err.response?.data?.message || "Nie udało się zmienić hasła.");
            } finally {
                setPwBusy(false);
            }
        },
        [pwCurrent, pwNew, pwConfirm, success]
    );

    const fullName = me ? [me.firstName, me.lastName].filter(Boolean).join(" ") : "";
    const graceHours = requests?.pdfGraceHours ?? 2;
    const consents = me?.consents || [];
    const activeConsent = consents.find((entry) => entry.code === legalKind);

    // Wiersze sekcji „Zgody i oświadczenia": etykiety i panel „Przeczytaj" z profilu
    // (me.consents), daty akceptacji Z GODZINĄ i numer wniosku z /portal/me/consents —
    // dopiero ten endpoint zwraca id wniosku, z którego backend generuje PDF potwierdzenia.
    // Zanim odpowie (albo po błędzie innym niż 404) zostaje dotychczasowa sama data z profilu.
    const consentBase =
        consents.length > 0
            ? consents
            : myConsents
                ? [
                      { code: "rodo", label: "Zgoda na przetwarzanie danych osobowych (RODO)" },
                      { code: "regulamin", label: "Regulamin wejścia i przebywania na terenie zakładu" },
                  ]
                : [];
    const consentRows = consentBase.map((consent) => {
        if (!myConsents) {
            return {
                ...consent,
                metaText: consent.accepted
                    ? `Zaakceptowano ${formatDateOnly(consent.acceptedAt)}${
                          consent.requestNumber ? ` • wniosek ${consent.requestNumber}` : ""
                      }`
                    : "Nie zaakceptowano — zgodę podpisuje się przy składaniu wniosku.",
            };
        }
        const stamp =
            consent.code === "rodo" ? myConsents.privacyAcceptedAt : myConsents.termsAcceptedAt;
        return {
            ...consent,
            accepted: Boolean(stamp),
            metaText: stamp
                ? `Zaakceptowano ${formatDateTime(stamp)}${
                      myConsents.number ? ` • wniosek ${myConsents.number}` : ""
                  }`
                : "Nie zaakceptowano — zgodę podpisuje się przy składaniu wniosku.",
        };
    });
    // Przyciski PDF mają sens dopiero, gdy jest co potwierdzać (co najmniej jedna data).
    const canConsentPdf = Boolean(
        myConsents?.requestId && (myConsents.privacyAcceptedAt || myConsents.termsAcceptedAt)
    );

    // Rama warunkowa: w panelu na pulpicie bez PortalShell (rama portalu to osobny serwis).
    // eslint-disable-next-line react/no-unstable-nested-components
    const ProfileWrap = ({ narrow, children }) =>
        embedded ? <div>{children}</div> : <PortalShell narrow={narrow}>{children}</PortalShell>;

    // ── Stany brzegowe: brak sesji / konto zablokowane / błąd wczytania ──
    if (!loading && (authRequired || blocked || (!me && error))) {
        return (
            <ProfileWrap narrow>
                <div className={styles.heroWrap}>
                    <PageHero
                        compact
                        badge="Biuro Przepustek / Moje konto"
                        title="Profil osoby zewnętrznej"
                        subtitle="Dane, zgody i historia przepustek w jednym miejscu."
                    />
                </div>

                <div className={styles.emptyState}>
                    <span className={styles.emptyIcon}>
                        {authRequired ? <FiLock /> : blocked ? <FiSlash /> : <FiAlertCircle />}
                    </span>
                    <h2 className={styles.emptyTitle}>
                        {authRequired
                            ? "Zaloguj się, aby zobaczyć swój profil"
                            : blocked
                                ? "Konto zablokowane"
                                : "Nie udało się wczytać profilu"}
                    </h2>
                    <p className={styles.emptyText}>
                        {authRequired
                            ? "Profil widzą wyłącznie osoby z kontem w portalu. Konto zakładasz z linku, który wysyłamy e-mailem po złożeniu wniosku — przepustka działa też bez konta."
                            : blocked || error}
                    </p>
                    <div className={styles.emptyActions}>
                        {authRequired && (
                            <Link to="/logowanie" className={styles.btnPrimary}>
                                <FiLogIn size={15} /> Zaloguj się
                            </Link>
                        )}
                        <Link to="/wniosek" className={styles.btnSecondary}>
                            Złóż wniosek o przepustkę
                        </Link>
                        {!authRequired && (
                            <button type="button" className={styles.btnSecondary} onClick={() => load(true)}>
                                <FiRefreshCw size={15} /> Spróbuj ponownie
                            </button>
                        )}
                    </div>
                </div>
            </ProfileWrap>
        );
    }

    return (
        <ProfileWrap>
            {!embedded && (
            <div className={styles.heroWrap}>
                <PageHero
                    badge="Biuro Przepustek / Moje konto"
                    title="Profil osoby zewnętrznej"
                    subtitle={
                        me
                            ? `${fullName}${me.email ? ` • ${me.email}` : ""}`
                            : "Dane, zgody i historia przepustek w jednym miejscu."
                    }
                >
                    {me && (
                        <div className={styles.heroPills}>
                            <span className={styles.heroPill}>
                                <span className={styles.heroPillIcon}>
                                    <FiFileText size={16} />
                                </span>
                                <span className={styles.heroPillText}>
                                    <span className={styles.heroPillValue}>{requests?.total ?? "—"}</span>
                                    <span className={styles.heroPillLabel}>Wnioski</span>
                                </span>
                            </span>
                            <span className={styles.heroPill}>
                                <span className={styles.heroPillIcon}>
                                    <FiShield size={16} />
                                </span>
                                <span className={styles.heroPillText}>
                                    <span className={styles.heroPillValue}>{requests?.active ?? "—"}</span>
                                    <span className={styles.heroPillLabel}>Aktywne przepustki</span>
                                </span>
                            </span>
                            <span className={styles.heroPill}>
                                <span className={styles.heroPillIcon}>
                                    <FiCalendar size={16} />
                                </span>
                                <span className={styles.heroPillText}>
                                    <span className={styles.heroPillValue}>{requests?.upcoming ?? "—"}</span>
                                    <span className={styles.heroPillLabel}>Nadchodzące</span>
                                </span>
                            </span>
                            <button
                                type="button"
                                className={styles.heroActionBtn}
                                onClick={() => load(true)}
                                disabled={loading}
                                aria-label="Odśwież profil"
                            >
                                <FiRefreshCw size={16} className={loading ? styles.spinning : ""} />
                            </button>
                        </div>
                    )}
                </PageHero>
            </div>
            )}

            {loading && !me && (
                <div className={styles.card}>
                    <div className={styles.loadingBox}>
                        <span className={styles.spinner} />
                        <span className={styles.loadingText}>Wczytywanie profilu…</span>
                    </div>
                </div>
            )}

            {me && (
                /* W oknie „Moje dane" nie ma bocznej kolumny — siatka nie może rezerwować
                   jej szerokości (treść stała w ~połowie okna przy pustej prawej stronie). */
                <div className={`${styles.layout} ${embedded ? styles.layoutEmbedded : ""}`}>
                    <div className={styles.mainCol}>
                        {error && (
                            <div className={`${styles.notice} ${styles.noticeWarning}`}>
                                <span className={styles.noticeIcon}>
                                    <FiAlertCircle size={16} />
                                </span>
                                <span className={styles.noticeBody}>
                                    <span className={styles.noticeTitle}>Dane mogą być nieaktualne</span>
                                    <span>{error}</span>
                                </span>
                            </div>
                        )}

                        {/* ── ZAKŁADKI: Przepustki / Moje dane ── */}
{!embedded && (
                        <div className={styles.tabStrip} role="tablist" aria-label="Sekcje profilu">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "przepustki"}
                                className={`${styles.tabBtn} ${tab === "przepustki" ? styles.tabBtnActive : ""}`}
                                onClick={() => setTab("przepustki")}
                            >
                                <FiFileText size={15} /> Przepustki
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "dane"}
                                className={`${styles.tabBtn} ${tab === "dane" ? styles.tabBtnActive : ""}`}
                                onClick={() => setTab("dane")}
                            >
                                <FiUser size={15} /> Moje dane
                            </button>
                        </div>
)}

                        {tab === "dane" && (
                        <>
                        {/* ── DANE OSOBOWE ──────────────────────────────────────────
                            Płaska sekcja z mikro-nagłówkiem zamiast osobnej karty, dane
                            jako wiersze definicji z cienkimi liniami (uwaga klienta
                            2026-08-08: „kartoniki" w wąskiej kolumnie okna Moje dane). */}
                        <section className={styles.sheetSection}>
                            <div className={styles.sheetHead}>
                                <h2 className={styles.sheetHeading}>
                                    <FiUser size={13} /> Dane osobowe
                                </h2>
                                <span className={styles.readOnlyTag}>
                                    <FiLock size={11} /> tylko do odczytu
                                </span>
                            </div>

                            {/* Mini układ „jak dowód": zdjęcie dokumentu po lewej,
                                tożsamość po prawej — właściciel od razu widzi, jakim
                                kompletem danych posługuje się portiernia. */}
                            <div className={styles.idRow}>
                                <span className={styles.idPhoto}>
                                    {photoUrl ? (
                                        <img
                                            src={photoUrl}
                                            alt="Zdjęcie dokumentu tożsamości z ostatniego wniosku"
                                            className={styles.idPhotoImg}
                                        />
                                    ) : (
                                        <span className={styles.idPhotoEmpty}>
                                            <FiCamera size={17} />
                                            {photoMissing ? "brak zdjęcia" : "wczytywanie…"}
                                        </span>
                                    )}
                                </span>
                                <span className={styles.idMain}>
                                    <span className={styles.idName}>{fullName || "—"}</span>
                                    {me.email && <span className={styles.idMeta}>{me.email}</span>}
                                    <span className={styles.idHint}>
                                        {photoUrl
                                            ? "Zdjęcie dokumentu z ostatniego wniosku — widzi je ochrona na portierni."
                                            : "Zdjęcie dokumentu dołącza się przy składaniu wniosku — przyspiesza kontrolę na bramie."}
                                    </span>
                                </span>
                            </div>

                            <dl className={styles.dataList}>
                                <DataRow label="Imię" value={me.firstName || "—"} />
                                <DataRow label="Nazwisko" value={me.lastName || "—"} />
                                <DataRow
                                    label="Adres e-mail"
                                    value={me.email || "—"}
                                    note={
                                        me.emailVerified ? (
                                            <span className={styles.noteOk}>
                                                <FiCheckCircle size={12} /> Potwierdzony
                                                {me.emailVerifiedAt
                                                    ? ` ${formatDateOnly(me.emailVerifiedAt)}`
                                                    : ""}
                                            </span>
                                        ) : (
                                            <span className={styles.noteWarn}>
                                                <FiAlertCircle size={12} /> Adres niepotwierdzony
                                            </span>
                                        )
                                    }
                                />
                                <DataRow
                                    label="Telefon"
                                    mono
                                    value={
                                            shown.phone
                                                ? me.phone || "—"
                                                : me.phoneMasked || me.phone || "—"
                                        }
                                        action={
                                            me.phone ? (
                                                <button
                                                    type="button"
                                                    className={`${styles.iconBtn} ${
                                                        shown.phone ? styles.iconBtnOn : ""
                                                    }`}
                                                    onClick={() => toggleField("phone")}
                                                    aria-pressed={shown.phone}
                                                    aria-label={
                                                        shown.phone ? "Ukryj numer telefonu" : "Pokaż numer telefonu"
                                                    }
                                                    title={shown.phone ? "Ukryj" : "Pokaż"}
                                                >
                                                    {shown.phone ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                                                </button>
                                            ) : null
                                        }
                                    />
                                    {/* Wiersz PESEL usunięty decyzją klienta 2026-08-22 — na dokumenty
                                        trafia data urodzenia, więc to ona stoi za „oczkiem". */}
                                    <DataRow
                                        label="Data urodzenia"
                                        mono
                                        value={
                                            shown.birthDate && revealed?.birthDate
                                                ? formatBirthDatePl(revealed.birthDate)
                                                : "••-••-••••"
                                        }
                                        action={
                                            me.hasBirthDate ? (
                                                <button
                                                    type="button"
                                                    className={`${styles.iconBtn} ${
                                                        shown.birthDate ? styles.iconBtnOn : ""
                                                    }`}
                                                    onClick={() => toggleField("birthDate")}
                                                    disabled={revealBusy}
                                                    aria-pressed={shown.birthDate}
                                                    aria-label={
                                                        shown.birthDate
                                                            ? "Ukryj datę urodzenia"
                                                            : "Pokaż datę urodzenia"
                                                    }
                                                    title={shown.birthDate ? "Ukryj" : "Pokaż (zapisywane w dzienniku)"}
                                                >
                                                    {shown.birthDate ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                                                </button>
                                            ) : null
                                        }
                                    />
                                    <DataRow
                                        label="Dokument tożsamości"
                                        mono
                                        value={
                                            shown.documentNumber && revealed?.documentNumber
                                                ? revealed.documentNumber
                                                : me.documentNumberMasked || "—"
                                        }
                                        action={
                                            me.hasDocumentNumber ? (
                                                <button
                                                    type="button"
                                                    className={`${styles.iconBtn} ${
                                                        shown.documentNumber ? styles.iconBtnOn : ""
                                                    }`}
                                                    onClick={() => toggleField("documentNumber")}
                                                    disabled={revealBusy}
                                                    aria-pressed={shown.documentNumber}
                                                    aria-label={
                                                        shown.documentNumber
                                                            ? "Ukryj numer dokumentu"
                                                            : "Pokaż numer dokumentu"
                                                    }
                                                    title={
                                                        shown.documentNumber
                                                            ? "Ukryj"
                                                            : "Pokaż (zapisywane w dzienniku)"
                                                    }
                                                >
                                                    {shown.documentNumber ? (
                                                        <FiEyeOff size={17} />
                                                    ) : (
                                                        <FiEye size={17} />
                                                    )}
                                                </button>
                                            ) : null
                                        }
                                    />
                                </dl>

                                <p className={styles.hint}>
                                    {me.reveal?.message ||
                                        "Pełny numer dokumentu i datę urodzenia pokazujemy dopiero po potwierdzeniu kodem wysłanym na adres e-mail konta — każde odsłonięcie jest zapisywane w dzienniku dostępu."}{" "}
                                    Odsłonięte wartości chowają się same po {Math.round(REVEAL_TIMEOUT_MS / 1000)}{" "}
                                    sekundach.
                                </p>

                                {/* Panel kodu odsłonięcia — pole jak przy kodzie SMS we wniosku:
                                    wyśrodkowane, o szerokości sześciu cyfr, z akcjami obok. */}
                                {revealChallenge && (
                                    <div
                                        className={styles.revealPanel}
                                        role="group"
                                        aria-label="Potwierdzenie kodem z e-maila"
                                    >
                                        <span className={styles.revealPanelTitle}>
                                            <FiMail size={14} />
                                            Kod wysłany na {revealChallenge.maskedEmail || "adres konta"}
                                        </span>
                                        <span className={styles.revealPanelText}>
                                            Wpisz 6-cyfrowy kod z wiadomości, aby odsłonić dane.
                                            {revealChallenge.devCode
                                                ? ` (tryb deweloperski — kod: ${revealChallenge.devCode})`
                                                : ""}
                                        </span>
                                        <span className={styles.revealCodeRow}>
                                            <input
                                                className={styles.revealInput}
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                                maxLength={6}
                                                placeholder="000000"
                                                aria-label="Kod odsłonięcia (6 cyfr)"
                                                value={revealCode}
                                                onChange={(event) =>
                                                    setRevealCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                                                }
                                            />
                                            <button
                                                type="button"
                                                className={styles.btnPrimary}
                                                onClick={confirmRevealCode}
                                                disabled={revealBusy || revealCode.length !== 6}
                                            >
                                                Potwierdź
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.btnGhost}
                                                onClick={() => {
                                                    setRevealChallenge(null);
                                                    setRevealCode("");
                                                    setPendingField(null);
                                                }}
                                                disabled={revealBusy}
                                            >
                                                Anuluj
                                            </button>
                                        </span>
                                    </div>
                                )}

                                {/* Korekta danych — dyskretny wiersz zamiast pudełka z przyciskiem:
                                    to informacja na marginesie, nie akcja panelu. */}
                                <p className={styles.hint}>
                                    {me.dataChange?.message ||
                                        "Danych osobowych nie zmienisz samodzielnie — trafiają na dokument przepustki i na portiernię."}{" "}
                                    Korektę zgłoś na{" "}
                                    <a
                                        className={styles.hintLink}
                                        href={`mailto:${
                                            me.dataChange?.contactEmail || "biuroprzepustek@krosno.com"
                                        }?subject=${encodeURIComponent(
                                            "Korekta danych osobowych w portalu przepustek"
                                        )}`}
                                    >
                                        {me.dataChange?.contactEmail || "biuroprzepustek@krosno.com"}
                                    </a>
                                    .
                                </p>
                        </section>

                        {/* ── ZGODY ───────────────────────────────────────────────── */}
                        <section className={styles.sheetSection}>
                            <div className={styles.sheetHead}>
                                <h2 className={styles.sheetHeading}>
                                    <FiShield size={13} /> Zgody i oświadczenia
                                </h2>
                            </div>
                                {myConsentsMissing ? (
                                    /* 404 z /portal/me/consents — żaden wniosek nie ma jeszcze zgód. */
                                    <p className={styles.hint}>
                                        Brak zapisanych zgód — zgody składasz przy pierwszym wniosku.
                                    </p>
                                ) : (
                                <div className={styles.consentList}>
                                    {consentRows.map((consent) => (
                                        <div className={styles.consentRow} key={consent.code}>
                                            <span
                                                className={`${styles.consentIcon} ${
                                                    consent.accepted ? styles.consentIconOk : styles.consentIconOff
                                                }`}
                                            >
                                                {consent.accepted ? (
                                                    <FiCheckCircle size={16} />
                                                ) : (
                                                    <FiAlertCircle size={16} />
                                                )}
                                            </span>
                                            <span className={styles.consentMain}>
                                                <span className={styles.consentTitle}>{consent.label}</span>
                                                <span className={styles.consentMeta}>{consent.metaText}</span>
                                            </span>
                                            <button
                                                type="button"
                                                className={`${styles.btnSecondary} ${styles.btnSm}`}
                                                onClick={() => setLegalKind(consent.code)}
                                            >
                                                <FiFileText size={14} /> Przeczytaj
                                            </button>
                                        </div>
                                    ))}

                                    {consentRows.length === 0 && (
                                        <p className={styles.hint}>
                                            Nie mamy jeszcze zapisanych zgód dla tego konta — pojawią się po
                                            złożeniu pierwszego wniosku.
                                        </p>
                                    )}

                                    {/* Potwierdzenie zgód z datami akceptacji — PDF generuje backend
                                        z wniosku, w którym zgody złożono (audyt obejmuje też podgląd). */}
                                    {canConsentPdf && (
                                        <div className={styles.consentActions}>
                                            <button
                                                type="button"
                                                className={`${styles.btnSecondary} ${styles.btnSm}`}
                                                onClick={previewConsentPdf}
                                                disabled={Boolean(consentPdfBusy)}
                                            >
                                                {consentPdfBusy === "preview" ? (
                                                    <FiRefreshCw size={14} className={styles.spinning} />
                                                ) : (
                                                    <FiEye size={14} />
                                                )}
                                                {consentPdfBusy === "preview" ? "Otwieranie…" : "Podgląd PDF"}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.btnSecondary} ${styles.btnSm}`}
                                                onClick={downloadConsentPdf}
                                                disabled={Boolean(consentPdfBusy)}
                                            >
                                                {consentPdfBusy === "download" ? (
                                                    <FiRefreshCw size={14} className={styles.spinning} />
                                                ) : (
                                                    <FiDownload size={14} />
                                                )}
                                                {consentPdfBusy === "download" ? "Pobieranie…" : "Pobierz PDF"}
                                            </button>
                                            <span className={styles.consentActionsHint}>
                                                Potwierdzenie zgód z datami akceptacji — generowane przez system.
                                            </span>
                                        </div>
                                    )}
                                </div>
                                )}
                        </section>

                        {/* ── WERYFIKACJA KONTAKTU ────────────────────────────────── */}
                        <section className={styles.sheetSection}>
                            <div className={styles.sheetHead}>
                                <h2 className={styles.sheetHeading}>
                                    <FiKey size={13} /> Weryfikacja kontaktu
                                </h2>
                            </div>
                                {/* E-mail */}
                                <div className={styles.verifyRow}>
                                    <span className={styles.verifyIcon}>
                                        <FiMail size={16} />
                                    </span>
                                    <div className={styles.verifyMain}>
                                        <span className={styles.verifyLabel}>Adres e-mail</span>
                                        <span className={styles.verifyValue}>{me.email || "—"}</span>
                                        {me.emailVerified ? (
                                            <span className={styles.noteOk}>
                                                <FiCheckCircle size={12} /> Potwierdzony
                                            </span>
                                        ) : (
                                            <span className={styles.noteWarn}>
                                                <FiAlertCircle size={12} /> Niepotwierdzony
                                            </span>
                                        )}
                                        {!me.emailVerified && emailStage === "code" && (
                                            <div className={styles.codeRow}>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    className={styles.codeInput}
                                                    value={emailCode}
                                                    onChange={(e) =>
                                                        setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                                                    }
                                                    placeholder="Kod z e-maila"
                                                    aria-label="Kod z wiadomości e-mail"
                                                />
                                                <button
                                                    type="button"
                                                    className={`${styles.btnPrimary} ${styles.btnSm}`}
                                                    onClick={verifyEmail}
                                                    disabled={emailBusy === "verify"}
                                                >
                                                    {emailBusy === "verify" ? "Sprawdzam…" : "Potwierdź"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {!me.emailVerified && (
                                        <button
                                            type="button"
                                            className={`${styles.btnSecondary} ${styles.btnSm}`}
                                            onClick={sendEmailCode}
                                            disabled={emailBusy === "send"}
                                        >
                                            {emailBusy === "send"
                                                ? "Wysyłam…"
                                                : emailStage === "code"
                                                ? "Wyślij ponownie"
                                                : "Zweryfikuj"}
                                        </button>
                                    )}
                                </div>

                                {/* Telefon */}
                                <div className={styles.verifyRow}>
                                    <span className={styles.verifyIcon}>
                                        <FiSmartphone size={16} />
                                    </span>
                                    <div className={styles.verifyMain}>
                                        <span className={styles.verifyLabel}>Numer telefonu</span>
                                        <span className={styles.verifyValue}>
                                            {me.phoneMasked || me.phone || "—"}
                                        </span>
                                        {!me.hasPhone ? (
                                            <span className={styles.noteWarn}>
                                                <FiAlertCircle size={12} /> Brak numeru — zmianę zgłoś do Biura Przepustek
                                            </span>
                                        ) : me.phoneVerified ? (
                                            <span className={styles.noteOk}>
                                                <FiCheckCircle size={12} /> Potwierdzony
                                            </span>
                                        ) : (
                                            <span className={styles.noteWarn}>
                                                <FiAlertCircle size={12} /> Niepotwierdzony
                                            </span>
                                        )}
                                        {me.hasPhone && !me.phoneVerified && phoneStage === "code" && (
                                            <div className={styles.codeRow}>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    className={styles.codeInput}
                                                    value={phoneCode}
                                                    onChange={(e) =>
                                                        setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                                                    }
                                                    placeholder="Kod z SMS"
                                                    aria-label="Kod z wiadomości SMS"
                                                />
                                                <button
                                                    type="button"
                                                    className={`${styles.btnPrimary} ${styles.btnSm}`}
                                                    onClick={verifyPhone}
                                                    disabled={phoneBusy === "verify"}
                                                >
                                                    {phoneBusy === "verify" ? "Sprawdzam…" : "Potwierdź"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {me.hasPhone && !me.phoneVerified && (
                                        <button
                                            type="button"
                                            className={`${styles.btnSecondary} ${styles.btnSm}`}
                                            onClick={sendPhoneCode}
                                            disabled={phoneBusy === "send"}
                                        >
                                            {phoneBusy === "send"
                                                ? "Wysyłam…"
                                                : phoneStage === "code"
                                                ? "Wyślij ponownie"
                                                : "Zweryfikuj SMS"}
                                        </button>
                                    )}
                                </div>

                        </section>

                        {/* ── BEZPIECZEŃSTWO KONTA — zwinięte ──────────────────────
                            Hasło zmienia się rzadko: trzy stale widoczne pola wydłużały
                            panel o pół ekranu i wyglądały jak obowiązkowy formularz.
                            Sekcja rozwija się świadomym kliknięciem na samym dole. */}
                        <section className={styles.sheetSection}>
                            <button
                                type="button"
                                className={styles.secToggle}
                                onClick={() => setSecurityOpen((open) => !open)}
                                aria-expanded={securityOpen}
                            >
                                <span className={styles.secToggleLabel}>
                                    <FiLock size={14} /> Bezpieczeństwo konta — zmiana hasła
                                </span>
                                <FiChevronDown
                                    size={15}
                                    className={`${styles.secChevron} ${
                                        securityOpen ? styles.secChevronOpen : ""
                                    }`}
                                />
                            </button>

                            {securityOpen && (
                                <div className={styles.secBody}>
                                    {!me.emailVerified ? (
                                        <p className={styles.hint}>
                                            <FiInfo size={13} /> Aby zmienić hasło, najpierw potwierdź
                                            adres e-mail w sekcji „Weryfikacja kontaktu" powyżej.
                                        </p>
                                    ) : (
                                        <form className={styles.pwForm} onSubmit={changePassword}>
                                            <input
                                                type="password"
                                                className={styles.pwInput}
                                                value={pwCurrent}
                                                onChange={(e) => setPwCurrent(e.target.value)}
                                                placeholder="Obecne hasło"
                                                autoComplete="current-password"
                                            />
                                            <input
                                                type="password"
                                                className={styles.pwInput}
                                                value={pwNew}
                                                onChange={(e) => setPwNew(e.target.value)}
                                                placeholder="Nowe hasło (min. 8 znaków)"
                                                autoComplete="new-password"
                                            />
                                            <input
                                                type="password"
                                                className={styles.pwInput}
                                                value={pwConfirm}
                                                onChange={(e) => setPwConfirm(e.target.value)}
                                                placeholder="Powtórz nowe hasło"
                                                autoComplete="new-password"
                                            />
                                            {pwError && <p className={styles.pwError}>{pwError}</p>}
                                            <button
                                                type="submit"
                                                className={`${styles.btnPrimary} ${styles.btnSm}`}
                                                disabled={pwBusy}
                                            >
                                                {pwBusy ? "Zapisywanie…" : "Zmień hasło"}
                                            </button>
                                        </form>
                                    )}
                                </div>
                            )}
                        </section>
                        </>
                        )}

                        {tab === "przepustki" && (
                        <>
                        {/* ── HISTORIA PRZEPUSTEK ─────────────────────────────────── */}
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardHeaderIcon}>
                                    <FiClock size={17} />
                                </span>
                                <div className={styles.cardHeaderMain}>
                                    <h2 className={styles.cardTitle}>Historia przepustek</h2>
                                    <p className={styles.cardSubtitle}>
                                        Wszystkie Twoje wnioski wraz ze statusem ważności.
                                    </p>
                                </div>
                            </div>

                            <div className={`${styles.cardBody} ${styles.cardBodyTight}`}>
                                {requestsError && (
                                    <div className={`${styles.notice} ${styles.noticeWarning}`}>
                                        <span className={styles.noticeIcon}>
                                            <FiAlertCircle size={16} />
                                        </span>
                                        <span className={styles.noticeBody}>
                                            <span>{requestsError}</span>
                                        </span>
                                    </div>
                                )}

                                {items.length > 0 && (
                                    <div className={styles.filterRow} role="group" aria-label="Filtr wniosków">
                                        {FILTERS.map((entry) => {
                                            const count =
                                                entry.key === "all"
                                                    ? items.length
                                                    : items.filter((item) => item.validity === entry.key).length;
                                            if (entry.key !== "all" && count === 0) return null;
                                            return (
                                                <button
                                                    key={entry.key}
                                                    type="button"
                                                    className={`${styles.filterChip} ${
                                                        filter === entry.key ? styles.filterChipActive : ""
                                                    }`}
                                                    onClick={() => setFilter(entry.key)}
                                                    aria-pressed={filter === entry.key}
                                                >
                                                    {entry.label}
                                                    <span className={styles.filterCount}>{count}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {visibleItems.length === 0 ? (
                                    <div className={styles.emptyBox}>
                                        <span className={styles.emptyIcon}>
                                            <FiInbox />
                                        </span>
                                        <h3 className={styles.emptyTitle}>
                                            {items.length === 0
                                                ? "Nie masz jeszcze żadnych wniosków"
                                                : "Brak wniosków w tym filtrze"}
                                        </h3>
                                        <p className={styles.emptyText}>
                                            {items.length === 0
                                                ? "Po złożeniu pierwszego wniosku znajdziesz tu jego status, terminy i dokument przepustki."
                                                : "Zmień filtr, aby zobaczyć pozostałe wnioski."}
                                        </p>
                                        {items.length === 0 && (
                                            <div className={styles.emptyActions}>
                                                <Link to="/wniosek" className={styles.btnPrimary}>
                                                    Złóż wniosek <FiArrowRight size={15} />
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className={styles.requestList}>
                                        {visibleItems.map((item) => {
                                            const token = trackingTokenOf(item);
                                            const statusTone = STATUS_TONE[item.status] || "neutral";
                                            const validityTone = VALIDITY_TONE[item.validity] || "neutral";
                                            return (
                                                <article className={styles.requestCard} key={item.number}>
                                                    <div className={styles.requestHead}>
                                                        <span className={styles.requestNumber}>{item.number}</span>
                                                        <span className={styles.requestBadges}>
                                                            <span
                                                                className={`${styles.badge} ${
                                                                    styles[`badge_${validityTone}`]
                                                                }`}
                                                            >
                                                                {item.validityLabel || item.validity}
                                                            </span>
                                                            <span
                                                                className={`${styles.badge} ${styles.badgeGhost} ${
                                                                    styles[`badge_${statusTone}`]
                                                                }`}
                                                            >
                                                                {item.statusLabel}
                                                            </span>
                                                        </span>
                                                    </div>

                                                    <div className={styles.requestMetaRow}>
                                                        <span className={styles.requestMeta}>
                                                            <span className={styles.requestMetaLabel}>Rodzaj</span>
                                                            <span className={styles.requestMetaValue}>
                                                                {item.kindLabel || "—"}
                                                            </span>
                                                        </span>
                                                        <span
                                                            className={`${styles.requestMeta} ${styles.requestMetaWide}`}
                                                        >
                                                            <span className={styles.requestMetaLabel}>Termin</span>
                                                            <span className={styles.requestMetaValue}>
                                                                {formatDatePl(item.dateFrom)}
                                                                {item.dateTo && item.dateTo !== item.dateFrom
                                                                    ? ` – ${formatDatePl(item.dateTo)}`
                                                                    : ""}
                                                                {item.hoursFrom
                                                                    ? ` • ${item.hoursFrom}${
                                                                          item.hoursTo ? `–${item.hoursTo}` : ""
                                                                      }`
                                                                    : ""}
                                                            </span>
                                                        </span>
                                                        {item.periods?.length > 1 && (
                                                            <span className={styles.requestMeta}>
                                                                <span className={styles.requestMetaLabel}>
                                                                    Okresy
                                                                </span>
                                                                <span className={styles.requestMetaValue}>
                                                                    {item.periods.length}
                                                                </span>
                                                            </span>
                                                        )}
                                                        {item.companyName && (
                                                            <span className={styles.requestMeta}>
                                                                <span className={styles.requestMetaLabel}>Firma</span>
                                                                <span className={styles.requestMetaValue}>
                                                                    {item.companyName}
                                                                </span>
                                                            </span>
                                                        )}
                                                        {item.hostName && (
                                                            <span className={styles.requestMeta}>
                                                                <span className={styles.requestMetaLabel}>
                                                                    Osoba przyjmująca
                                                                </span>
                                                                <span className={styles.requestMetaValue}>
                                                                    {item.hostName}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>

                                                    {(item.status === 3 || item.status === 4) &&
                                                        (item.cancellationReason || item.decisionNote) && (
                                                            <p className={styles.requestNote}>
                                                                <FiXCircle size={13} />
                                                                {item.cancellationReason || item.decisionNote}
                                                            </p>
                                                        )}

                                                    <div className={styles.requestActions}>
                                                        {/* Kod QR pokazujemy TYLKO gdy przepustka jest AKTYWNA
                                                            (w okresie ważności) — wymóg klienta. */}
                                                        {item.validity === "aktywna" && item.qrToken && (
                                                            <button
                                                                type="button"
                                                                className={`${styles.btnPrimary} ${styles.btnSm}`}
                                                                onClick={() => setQrPass(item)}
                                                            >
                                                                <FiMaximize2 size={14} /> Kod QR
                                                            </button>
                                                        )}
                                                        {token ? (
                                                            <Link
                                                                to={`/wniosek/status/${encodeURIComponent(token)}`}
                                                                className={`${styles.btnSecondary} ${styles.btnSm}`}
                                                            >
                                                                <FiEye size={14} /> Podgląd statusu
                                                            </Link>
                                                        ) : (
                                                            <span className={styles.hint}>
                                                                Podgląd statusu niedostępny dla tego wniosku.
                                                            </span>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className={`${styles.btnPrimary} ${styles.btnSm}`}
                                                            onClick={() => downloadPdf(item)}
                                                            disabled={
                                                                !item.canDownloadPdf ||
                                                                !token ||
                                                                pdfBusy === item.number
                                                            }
                                                            title={pdfHint(item, graceHours)}
                                                        >
                                                            {pdfBusy === item.number ? (
                                                                <FiRefreshCw size={14} className={styles.spinning} />
                                                            ) : (
                                                                <FiDownload size={14} />
                                                            )}
                                                            {pdfBusy === item.number ? "Pobieranie…" : "Pobierz PDF"}
                                                        </button>
                                                    </div>

                                                    <p className={styles.requestHint}>{pdfHint(item, graceHours)}</p>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                        </>
                        )}
                    </div>

                    {/* ── PANEL BOCZNY ────────────────────────────────────────────── */}
                    {!embedded && (
                    <aside className={styles.aside}>
                        <div className={`${styles.card} ${styles.asideSticky}`}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardHeaderIcon}>
                                    <FiShield size={17} />
                                </span>
                                <div className={styles.cardHeaderMain}>
                                    <h2 className={styles.cardTitle}>Twoje konto</h2>
                                    <p className={styles.cardSubtitle}>Skrót najważniejszych informacji.</p>
                                </div>
                            </div>
                            <div className={`${styles.cardBody} ${styles.cardBodyTight}`}>
                                <div className={styles.factList}>
                                    <div className={styles.factRow}>
                                        <span className={styles.factLabel}>Konto założone</span>
                                        <span className={styles.factValue}>
                                            {me.accountCreatedAt ? formatDateOnly(me.accountCreatedAt) : "—"}
                                        </span>
                                    </div>
                                    <div className={styles.factRow}>
                                        <span className={styles.factLabel}>Ostatni wniosek</span>
                                        <span className={styles.factValue}>
                                            {me.lastRequestAt ? formatDateOnly(me.lastRequestAt) : "—"}
                                        </span>
                                    </div>
                                    <div className={styles.factRow}>
                                        <span className={styles.factLabel}>Adres e-mail</span>
                                        <span className={styles.factValue}>
                                            {me.emailVerified ? "Potwierdzony" : "Niepotwierdzony"}
                                        </span>
                                    </div>
                                    <div className={styles.factRow}>
                                        <span className={styles.factLabel}>PDF przepustki</span>
                                        <span className={styles.factValue}>
                                            do {graceHours} godz. po wizycie
                                        </span>
                                    </div>
                                </div>

                                <Link to="/wniosek" className={`${styles.btnPrimary} ${styles.btnBlock}`}>
                                    Nowy wniosek o przepustkę <FiArrowRight size={15} />
                                </Link>

                                <div className={`${styles.notice} ${styles.noticeInfo}`}>
                                    <span className={styles.noticeIcon}>
                                        <FiLock size={16} />
                                    </span>
                                    <span className={styles.noticeBody}>
                                        <span>
                                            Profil pokazuje dane wyłącznie zamaskowane. Jeśli korzystasz ze
                                            wspólnego komputera, po zakończeniu wyloguj się.
                                        </span>
                                    </span>
                                </div>

                                <div className={styles.contactRow}>
                                    <span className={styles.contactIcon}>
                                        <FiMail size={15} />
                                    </span>
                                    <span className={styles.contactBody}>
                                        <span className={styles.contactLabel}>Biuro Przepustek</span>
                                        <a
                                            className={styles.contactLink}
                                            href={`mailto:${
                                                me.dataChange?.contactEmail || "biuroprzepustek@krosno.com"
                                            }`}
                                        >
                                            {me.dataChange?.contactEmail || "biuroprzepustek@krosno.com"}
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </aside>
                    )}
                </div>
            )}

            {/* Ten sam panel, którym akceptuje się dokumenty we wniosku — tutaj do wglądu.
                Akceptacji NIE da się złożyć z profilu: zgoda dotyczy konkretnego wniosku. */}
            <LegalDrawer
                isOpen={Boolean(legalKind)}
                onClose={() => setLegalKind(null)}
                kind={legalKind || "rodo"}
                accepted={Boolean(activeConsent?.accepted)}
                onAccept={() => {
                    if (!activeConsent?.accepted) {
                        info("Zgodę rejestrujemy przy składaniu wniosku — tutaj dokument jest do wglądu.");
                    }
                    setLegalKind(null);
                }}
            />

            {/* Kod QR aktywnej przepustki na pełny ekran (do okazania ochronie). */}
            {qrPass?.qrToken && (
                <div
                    className={styles.qrOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Kod QR przepustki"
                    onClick={() => setQrPass(null)}
                >
                    <button
                        type="button"
                        className={styles.qrOverlayClose}
                        onClick={() => setQrPass(null)}
                        aria-label="Zamknij podgląd kodu QR"
                    >
                        <FiX size={22} />
                    </button>
                    <div className={styles.qrOverlayInner} onClick={(event) => event.stopPropagation()}>
                        <span className={styles.qrOverlayNumber}>Przepustka {qrPass.number}</span>
                        <canvas ref={qrCanvasRef} className={styles.qrOverlayCanvas} aria-label="Kod QR przepustki" />
                        <p className={styles.qrOverlayHint}>Okaż ten kod ochronie na portierni.</p>
                    </div>
                </div>
            )}

            {/* ── Podgląd PDF potwierdzenia zgód (modal) ──
                Zamknięcie WYŁĄCZNIE przyciskami (warunek W-10) — klik w tło nie zamyka.
                Okno 1:1 z podglądu dokumentów w PortalAccountsPage. */}
            {consentPreview && (
                <div
                    className={styles.previewOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-label={consentPreview.title}
                >
                    <div className={styles.previewModal}>
                        <div className={styles.previewHead}>
                            <span className={styles.previewTitle}>
                                {consentPreview.title}
                                {consentPreview.subtitle ? (
                                    <span className={styles.previewSubtitle}> • {consentPreview.subtitle}</span>
                                ) : null}
                            </span>
                            <div className={styles.previewActions}>
                                <button
                                    type="button"
                                    className={`${styles.btnSecondary} ${styles.btnSm}`}
                                    onClick={() =>
                                        triggerDownload(consentPreview.blob, consentPreview.fileName)
                                    }
                                >
                                    <FiDownload size={14} /> Pobierz
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.btnSecondary} ${styles.btnSm}`}
                                    onClick={closeConsentPreview}
                                >
                                    <FiX size={14} /> Zamknij
                                </button>
                            </div>
                        </div>
                        <div className={styles.previewBody}>
                            <iframe
                                src={consentPreview.url}
                                title={consentPreview.title}
                                className={styles.previewFrame}
                            />
                        </div>
                    </div>
                </div>
            )}
        </ProfileWrap>
    );
};

export default PortalProfilePage;
